import { CSSProperties, ReactNode, useImperativeHandle, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Ranking, rankings } from '@tanstack/match-sorter-utils';
import {
  ColumnDef,
  FilterFnOption,
  getCoreRowModel,
  getFilteredRowModel,
  Row,
  TableMeta,
  useReactTable,
} from '@tanstack/react-table';

import { DisplayModes } from 'services/webedi/types';
import { cn } from 'support/helpers/cn/cn';
import { WithRef } from 'support/types/withRef';
import { Button } from 'components/Form/Button/Button';

import { useDataTableContext } from './contexts/DataTableContext';
import { useBulkActions } from './hooks/useBulkActions/useBulkActions';
import { FocusedGroupExpander } from './hooks/useCollapsedGroups/FocusedGroupExpander';
import { useCollapsedGroups } from './hooks/useCollapsedGroups/useCollapsedGroups';
import { useColumnLayout } from './hooks/useColumnLayout';
import { useColumnWidth } from './hooks/useColumnWidth';
import { useDataRenderMode } from './hooks/useDataRenderMode';
import { calculateDataRowPixelHeight } from './hooks/useEmbeddedTableRows/helpers';
import { useGlobalFilter } from './hooks/useGlobalFilter';
import { useRowSelection } from './hooks/useRowSelection';
import { useVirtualizer } from './hooks/useVirtualizer';
import { collectParentPaths } from './utils/pathUtils';
import { HeaderCell } from './cells/HeaderCell';
import { BulkEditDialog } from './components/BulkEditDialog/BulkEditDialog';
import { ColGroup } from './components/ColGroup';
import { OverflowTooltip } from './components/OverflowTooltip/OverflowTooltip';
import { Search } from './components/Search';
import { SelectionWidget } from './components/SelectionWidget/SelectionWidget';
import { TableBody } from './components/TableBody';
import { TableHeader } from './components/TableHeader';
import { createFuzzyFilter } from './fuzzyFilter';
import { DataRenderModes } from './types';

export type DataTableProps<TData, TValue> = {
  /**
   * Component to be displayed in the footer
   */
  footerMenu?: ReactNode;
  /** Provide array of pinned columns ids. Pins to the left. */
  pinnedColumns?: Array<string>;
  /** Enable to show cell borders. */
  showCellBorders?: boolean;
  /**
   * Provide '@tanstack/react-table' configuration
   * @see https://tanstack.com/table/latest/docs/guide/column-defs
   */
  columns: Array<ColumnDef<TData, TValue>>;
  /** Provide table data */
  data: Array<TData> | undefined;
  /** Provide base data for diff comparison. Diff mode activates when both baseData and revisedData are present. */
  baseData?: Array<TData>;
  /** Provide revised data for diff comparison. Diff mode activates when both baseData and revisedData are present. */
  revisedData?: Array<TData>;
  /** Empty state component */
  emptyState?: ReactNode;
  /**
   * Provide '@tanstack/react-table' meta information
   * @see https://tanstack.com/table/latest/docs/api/core/table#meta
   */
  meta: TableMeta<TData>;
  /** Set render mode for the data. Allows to override default 'auto' setting */
  dataRenderMode?: keyof typeof DataRenderModes;
  /**
   * Make the scroll container fill 100% of its parent's height via flexbox instead of a fixed
   * viewport-relative height, so the table's own internal scroll (not the page) absorbs whatever
   * space sibling elements (e.g. a sticky footer sized by its own content) don't take. The parent
   * must itself be a bounded-height flex column for this to resolve to a real height.
   *
   * `footerMenu` then renders inside the scroll container, after the table: a footer left below a
   * bounded box would always be on screen, so an infinite-scroll sentinel there would never wait
   * for the user to reach the end of the rows.
   */
  fillAvailableHeight?: boolean;
  /**
   * Skip re-rendering rows whose own data did not change when others are added, removed or edited.
   * Only for tables whose cells take their values from the form (`useWatch`/`register`) or from
   * context, never from the row data they were rendered with: a skipped row keeps the row object
   * it last rendered. The table's `meta` must be referentially stable for this to have any effect.
   */
  memoizeRows?: boolean;
  /** Change a number of non-visible items rendered in virtualized mode. Needed for debugging */
  overscan?: number;
  // TODO: improve type
  /**
   * Provide a custom filtering function for global search
   * @see https://tanstack.com/table/latest/docs/framework/react/examples/filters-fuzzy
   */
  globalFilterFn?: FilterFnOption<TData>;
  searchPositionStrategy?: 'relative' | 'absolute';
  /** Enable to display search input on the top of the table */
  enableGlobalSearch?: boolean;
  /**/
  globalSearchThreshold?: Ranking;
  /**
   * Provide a function to conditionally enable or disable specific row for selection
   * @example
   * // enable selection conditionally per row
   * <DataTable rowSelectionFn={row => row.original.age > 18} />
   */
  rowSelectionFn?: (row: Row<TData>) => boolean;

  /**
   * Provide a predicate to hide specific top-level rows from display without touching the
   * underlying data. Filtering happens on the materialized rows, so each surviving row keeps its
   * original `row.index` and write-back paths (`<dataPrefix>.<row.index>.<field>`) stay correct.
   * Only whole top-level rows are hidden; embedded rows cannot be hidden individually.
   * @example
   * <DataTable hideRow={row => !row.original.unit_identifier} />
   */
  hideRow?: (row: Row<TData>) => boolean;

  /**
   * Component to be displayed in the header
   */
  headerMenu?: ReactNode;
  /**
   * Enable equal width columns
   */
  equalWidthColumns?: boolean;

  /**
   * Table id
   */
  id?: string;
};

export type DataTableHandle = {
  scrollToIndex: (index: number) => void;
  /**
   * Expands every collapsed column group of the row at a DATA row index.
   *
   * Error navigation locates a field by querying `[data-field]` in the DOM, so a field inside a
   * collapsed group cannot be found. Callers that scroll to a field must expand first.
   */
  expandRow: (index: number) => void;
};

const TABLE_HEIGHT = 'calc(100svh - 180px)';
const TABLE_MAX_HEIGHT = '940px'; // 20 rows

const TableInner = <TData, TValue>({
  columns,
  data = [],
  baseData,
  revisedData,
  showCellBorders = true,
  emptyState,
  meta,
  pinnedColumns = [],
  headerMenu,
  footerMenu,
  dataRenderMode: dataRenderModeProp = DataRenderModes.auto,
  fillAvailableHeight = false,
  memoizeRows = false,
  overscan = 10,
  globalFilterFn: globalFilterFnProp,
  enableGlobalSearch = true,
  searchPositionStrategy = 'relative',
  globalSearchThreshold = rankings.CONTAINS,
  rowSelectionFn,
  hideRow,
  equalWidthColumns,
  id,
  ref,
}: WithRef<DataTableProps<TData, TValue>, DataTableHandle>) => {
  const { t } = useTranslation();
  const globalFilterFn = useMemo(() => {
    if (!globalFilterFnProp) {
      return createFuzzyFilter<TData>(globalSearchThreshold);
    }
    return globalFilterFnProp;
  }, [globalFilterFnProp, globalSearchThreshold]);

  const { collapsedGroups, toggleGroupCollapsed, isGroupCollapsed, expandRow, expandGroupForField } =
    useCollapsedGroups({ data });

  const enrichedMeta = useMemo(() => {
    const withDiff =
      baseData && revisedData
        ? { ...meta, baseData: baseData as Array<unknown>, revisedData: revisedData as Array<unknown> }
        : meta;

    return { ...withDiff, collapsedGroups, onToggleGroupCollapsed: toggleGroupCollapsed };
  }, [meta, baseData, revisedData, collapsedGroups, toggleGroupCollapsed]);

  const { globalFilter, setGlobalFilter, handleResetFilter } = useGlobalFilter();
  const { rowSelection, enableRowSelection, setRowSelection, displayWidget, clearSelection } = useRowSelection({
    columns,
    rowSelectionFn,
  });

  const table = useReactTable({
    data,
    columns,
    state: {
      rowSelection,
      columnPinning: {
        left: pinnedColumns,
      },
      globalFilter,
    },
    enableRowSelection,
    onRowSelectionChange: setRowSelection,
    meta: enrichedMeta,
    getCoreRowModel: getCoreRowModel(),
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn,
    getFilteredRowModel: getFilteredRowModel(),
  });

  const { rows } = table.getRowModel();
  // Filtering the materialized rows (not the input data) hides rows for display while preserving
  // each surviving row's `row.index`, so field write-back paths stay correct. dataRowHeights must
  // be rebuilt from the same filtered set to keep the virtualizer index-consistent.
  const visibleRows = useMemo(() => (hideRow ? rows.filter((row) => !hideRow(row)) : rows), [rows, hideRow]);
  const dataRenderMode = useDataRenderMode({ mode: dataRenderModeProp, rows: visibleRows });

  const parentColumnPaths = useMemo(() => collectParentPaths(columns as Array<ColumnDef<TData, unknown>>), [columns]);
  const enableEmbeddedRowActions = meta?.mode === DisplayModes.create || meta?.mode === DisplayModes.edit;
  const dataRowHeights = useMemo(
    () =>
      visibleRows.map((row) =>
        calculateDataRowPixelHeight(
          row.original,
          parentColumnPaths,
          enableEmbeddedRowActions,
          new Set(parentColumnPaths.filter((path) => isGroupCollapsed(row.index, path))),
        ),
      ),
    [visibleRows, parentColumnPaths, enableEmbeddedRowActions, isGroupCollapsed],
  );

  const { parentRef, virtualizer, before, after, virtualRows } = useVirtualizer({
    rows: visibleRows,
    overscan,
    dataRenderMode,
    dataRowHeights,
  });

  useImperativeHandle(
    ref,
    () => ({
      // Callers pass a DATA row index (the `section.<index>` from a field path), but the
      // virtualizer is built over visibleRows — translate, or hidden/search-filtered rows above
      // the target skew the scroll position.
      scrollToIndex: (index: number) => {
        if (visibleRows.length === 0) {
          return;
        }
        const visiblePosition = visibleRows.findIndex((row) => row.index >= index);
        virtualizer.scrollToIndex(visiblePosition === -1 ? visibleRows.length - 1 : visiblePosition, {
          align: 'start',
        });
      },
      expandRow,
    }),
    [virtualizer, visibleRows, expandRow],
  );

  const hasRows = visibleRows.length > 0;
  const footer = splitFooter(footerMenu, fillAvailableHeight);
  const scrollStyle = getScrollStyle(dataRenderMode, fillAvailableHeight);

  const { setValue, reshapeKey } = useDataTableContext();
  const { isBulkEditModalOpen, handleBulkEditModalClose, bulkColumns, handleBulkEdit, handleBulkEditButtonClick } =
    useBulkActions({
      columns,
      rowSelection,
      setTableData: (value) => {
        setValue(undefined, value);
      },
      tableData: data,
    });

  const columnsWidth = useColumnWidth(columns, Boolean(equalWidthColumns));
  const columnWidths = useColumnLayout(
    columns,
    data,
    reshapeKey,
    parentColumnPaths,
    {
      isEditable: enableEmbeddedRowActions,
      baseData: enrichedMeta.baseData as Array<TData> | undefined,
      revisedData: enrichedMeta.revisedData as Array<TData> | undefined,
      dataPrefix: enrichedMeta.dataPrefix,
    },
    parentRef,
  );
  const bodyVirtualizationConfig = useMemo(() => ({ before, after, virtualRows }), [after, before, virtualRows]);
  return (
    <>
      <BulkEditDialog
        isOpen={isBulkEditModalOpen}
        closeSelf={handleBulkEditModalClose}
        inputsConfig={bulkColumns}
        onApply={handleBulkEdit}
      />
      <FocusedGroupExpander dataPrefix={meta?.dataPrefix} expandGroupForField={expandGroupForField} />
      <div className={getRootClassName(fillAvailableHeight)}>
        <TableControls
          dataLength={data.length}
          enableGlobalSearch={enableGlobalSearch}
          filter={globalFilter}
          headerMenu={headerMenu}
          onFilterChange={setGlobalFilter}
          searchPositionStrategy={searchPositionStrategy}
        />
        <div className="relative">
          {displayWidget && (
            <SelectionWidget
              rowSelection={rowSelection}
              handleBulkEditButtonClick={handleBulkEditButtonClick}
              clearSelection={clearSelection}
            />
          )}
        </div>
        <div
          ref={parentRef}
          style={scrollStyle}
          id={id}
          className={getScrollContainerClassName(hasRows, fillAvailableHeight)}
        >
          <OverflowTooltip containerRef={parentRef} />
          {hasRows ? (
            <table className="w-full table-fixed border-separate border-spacing-0 text-xs">
              {!equalWidthColumns && <ColGroup table={table} columnWidths={columnWidths} />}
              <TableHeader<TData> table={table} showCellBorders={showCellBorders} columnsWidth={columnsWidth} />
              <TableBody<TData, TValue>
                key={reshapeKey}
                dataRenderMode={dataRenderMode}
                virtualizationConfig={bodyVirtualizationConfig}
                columns={columns}
                showCellBorders={showCellBorders}
                columnsWidth={columnsWidth}
                rows={visibleRows}
                parentColumnPaths={parentColumnPaths}
                enableEmbeddedRowActions={enableEmbeddedRowActions}
                isGroupCollapsed={isGroupCollapsed}
                memoizeRows={memoizeRows}
                tableMeta={enrichedMeta}
              />
            </table>
          ) : (
            <div className="flex w-full flex-col items-center gap-3 rounded-md bg-gray-50 p-4 text-sm text-gray-500">
              <div>{!globalFilter ? emptyState : t('common:dataTable.search.emptyState')}</div>
              {globalFilter && (
                <Button variant="secondary" onClick={handleResetFilter} analyticsId="table_search_clear">
                  {t('common:dataTable.search.clearSearch')}
                </Button>
              )}
            </div>
          )}
          {footer.insideScroll}
        </div>
        {footer.belowScroll}
      </div>
    </>
  );
};

export const DataTable = Object.assign(
  TableInner as <TData, TValue>(props: WithRef<DataTableProps<TData, TValue>, DataTableHandle>) => ReactNode,
  { HeaderCellBuilder: HeaderCell },
);

const getRootClassName = (fillAvailableHeight: boolean): string =>
  cn('w-full', fillAvailableHeight && 'flex h-full min-h-0 flex-col');

const getScrollContainerClassName = (hasRows: boolean, fillAvailableHeight: boolean): string =>
  cn(
    'relative overflow-auto rounded-md',
    { 'border border-gray-200': hasRows },
    fillAvailableHeight && 'min-h-0 flex-1',
  );

const getScrollStyle = (
  dataRenderMode: DataRenderModes.plain | DataRenderModes.virtualized | DataRenderModes.scrollable,
  fillAvailableHeight: boolean,
): CSSProperties => {
  // The flex classes on the wrapper and this container (see `fillAvailableHeight` above) resolve
  // the actual height; a fixed or capped inline height here would fight that layout.
  if (fillAvailableHeight) {
    return {};
  }
  if (dataRenderMode !== DataRenderModes.plain) {
    return { height: TABLE_HEIGHT, maxHeight: TABLE_MAX_HEIGHT };
  }
  return { maxHeight: TABLE_HEIGHT };
};

const splitFooter = (footerMenu: ReactNode, fillAvailableHeight: boolean) => {
  const footer = footerMenu ? <div className="mt-3">{footerMenu}</div> : null;
  return fillAvailableHeight
    ? { insideScroll: footer, belowScroll: null }
    : { insideScroll: null, belowScroll: footer };
};

type TableControlsProps = {
  dataLength: number;
  enableGlobalSearch: boolean;
  filter: string;
  headerMenu: ReactNode;
  onFilterChange: (filter: string) => void;
  searchPositionStrategy: 'relative' | 'absolute';
};

const TableControls = ({
  dataLength,
  enableGlobalSearch,
  filter,
  headerMenu,
  onFilterChange,
  searchPositionStrategy,
}: TableControlsProps) => {
  const showGlobalSearch = enableGlobalSearch && dataLength > 0;

  return (
    <div
      className={cn('inset-x-0 top-0', searchPositionStrategy, {
        'mt-3': searchPositionStrategy === 'absolute',
      })}
    >
      {showGlobalSearch || headerMenu ? (
        <div className="mb-2 flex justify-end gap-2">
          {showGlobalSearch ? <Search filter={filter} onFilterChange={onFilterChange} /> : null}
          {headerMenu ? headerMenu : null}
        </div>
      ) : null}
    </div>
  );
};
